// Ported from MedSnapp Bleep (viewers/ecg/Paper.tsx), background IP licensed to MedSnapp: the 12-lead as a
// machine prints it. Paper, 1 mm and 5 mm grid, grain and trace are drawn in one canvas so the trace stays
// calibrated at 25 mm/s and 10 mm/mV (4 px per mm at scale 1). Standard 3×4 layout, 2.5 s per lead, a 10 s
// lead II rhythm strip and a 1 mV calibration pulse per row. The header is the machine's measurement block
// (rate, PR, QRS, QT/QTc, axes; measure.ts) and the paper settings: no patient details and no machine
// interpretation (the interpretation is the question). The drawing takes any scale and can be turned a quarter
// for a phone held upright.
import type { EcgParams, Lead } from './types';
import { sampleLead } from './synth';
import { measure, type MeasureInput } from './measure';

const MM = 4;
const PX_S = 25 * MM;
const PX_MV = 10 * MM;
const SEG_S = 2.5;
const SEG = SEG_S * PX_S;
const X0 = 8 * MM;
export const PAPER_WIDTH = X0 + 4 * SEG + 2 * MM;
const ROW = 21 * MM;
const ROWS: Lead[][] = [['I', 'aVR', 'V1', 'V4'], ['II', 'aVL', 'V2', 'V5'], ['III', 'aVF', 'V3', 'V6']];
const FONT_STACK = "'IBM Plex Mono', ui-monospace, monospace";
export const PAPER_FONT = `500 14px ${FONT_STACK}`;

/** The printed grid of thermal ECG paper, on the cream paper of Rounds' slips. */
const GRID_MINOR = '#F1CBC6';
const GRID_MAJOR = '#DD8F88';

// ---------------------------------------------------------------------------------------------------------------
// The header: the measurement block in two columns, then the paper settings, in the machine's monospaced type.
// ---------------------------------------------------------------------------------------------------------------
/** The machine's type and line, in paper pixels (3.5 mm type on a 4.25 mm line). */
const HEAD_FONT = 14;
const HEAD_LINE = 17;
/** On a sheet drawn small (a phone's answer area) the header type grows so it never prints below this many CSS pixels... */
const HEAD_MIN_CSS = 9;
/** ...but never so large that the longest row stops fitting across the sheet. */
const HEAD_MAX_FONT = 28;
/** Rows in the header, the same for every rhythm (missing values print as ---), so the sheet never changes shape. */
export const HEADER_ROWS = 4;
const SETTINGS = '25 mm/s   10 mm/mV   0.05–150 Hz';
const NONE = '---';

/**
 * The header type size in paper pixels for a sheet drawn at `scale` CSS pixels per paper pixel: the machine's 14 px, enlarged on
 * a small sheet so it stays readable (at least HEAD_MIN_CSS on the screen). The stage and the enlarged sheet always get 14 px.
 */
export function headerFont(scale = 1): number {
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return Math.min(HEAD_MAX_FONT, Math.max(HEAD_FONT, HEAD_MIN_CSS / s));
}

/** The lead names: the machine's 14 px, never below this many CSS pixels on the screen (a phone's card), nor above 28 px. */
const LEAD_FONT = 14;
const LEAD_MIN_CSS = 9;
const LEAD_MAX_FONT = 28;
/** The lead-name type size in paper pixels for a sheet drawn at `scale`, as headerFont does for the header. */
export function leadFont(scale = 1): number {
  const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return Math.min(LEAD_MAX_FONT, Math.max(LEAD_FONT, LEAD_MIN_CSS / s));
}

const column1 = (label: string, v: number | undefined, unit: string) => `${label.padEnd(13)}${v === undefined ? ` ${NONE}` : `${String(v).padStart(4)} ${unit}`}`;
const column2 = (label: string, v: string) => `${label.padEnd(11)}${v.padStart(14)}`;
/** The P, QRS and T axes, each right-aligned in four places and always a space apart (an axis such as −148 takes all four). */
const axes = (...vs: Array<number | undefined>) => vs.map((v) => (v === undefined ? NONE : String(v)).padStart(4)).join(' ');

/**
 * The header rows as the machine prints them, in a monospaced type so the columns line up: the measurement block (rate, PR and
 * QRS down the left; QT/QTc and the P-R-T axes beside them), then the paper settings. Values the rhythm has not got are ---.
 * Nothing that gives the answer away.
 */
export function headerLines(params: MeasureInput): string[] {
  const m = measure(params);
  const qt = m.qt !== undefined && m.qtc !== undefined ? `${m.qt}/${m.qtc} ms` : NONE;
  return [
    `${column1('Vent. rate', m.rate, 'bpm').padEnd(25)}${column2('QT/QTc', qt)}`,
    `${column1('PR interval', m.pr, 'ms').padEnd(25)}${column2('P-R-T axes', axes(m.pAxis, m.qrsAxis, m.tAxis))}`,
    column1('QRS duration', m.qrs, 'ms'),
    SETTINGS,
  ];
}

/** The header's height in paper pixels for its rows at a scale: top margin, the rows, a little air, up to the next 5 mm line. */
function headHeight(headerLineCount: number, scale?: number): number {
  const line = (headerFont(scale) * HEAD_LINE) / HEAD_FONT;
  return Math.ceil((8 + headerLineCount * line + 4) / (5 * MM)) * 5 * MM;
}
/** Where the first row of leads starts on the sheet, in paper pixels (below the header). */
export const paperHead = headHeight;
/** The margin under the rhythm strip: 5 mm, room for a deep complex in lead II (a broad QS of 1.3 mV) before the paper's edge. */
const FOOT = 5 * MM;

/** Paper height (in paper pixels) for a number of header lines, drawn at `scale` (the header grows on a small sheet). */
export function paperHeight(headerLineCount: number, scale?: number): number {
  return headHeight(headerLineCount, scale) + 4 * ROW + FOOT;
}

/** Each lead's 2.5 s cell on the 3 × 4 sheet, in paper pixels (the lead II rhythm strip underneath is left out). */
export function paperCells(headerLineCount: number, scale?: number): Record<Lead, Cell> {
  const head = headHeight(headerLineCount, scale);
  const out = {} as Record<Lead, Cell>;
  ROWS.forEach((row, r) => row.forEach((lead, c) => { out[lead] = { x: X0 + c * SEG, y: head + r * ROW, w: SEG, h: ROW }; }));
  return out;
}

function hash01(n: number): number {
  let x = Math.imul(n | 0, 0x9e3779b1) ^ 0x85ebca6b;
  x = Math.imul(x ^ (x >>> 15), 0x2c1b3c6d);
  x = Math.imul(x ^ (x >>> 12), 0x297a2d39);
  x ^= x >>> 15;
  return (x >>> 0) / 4294967296;
}

/** Thermal paper grain and a faint fibre along the print direction, as a repeating tile. */
let textureTile: HTMLCanvasElement | null = null;
function texture(ctx: CanvasRenderingContext2D): CanvasPattern | null {
  if (!textureTile) {
    const T = 160;
    const tile = document.createElement('canvas');
    tile.width = T; tile.height = T;
    const c = tile.getContext('2d');
    if (!c) return null;
    for (let i = 0; i < 2600; i++) {
      const x = hash01(i * 3 + 1) * T, y = hash01(i * 3 + 2) * T, k = hash01(i * 3 + 3);
      c.fillStyle = k < 0.5 ? `rgba(120, 92, 60, ${0.025 + 0.04 * k})` : `rgba(255, 255, 255, ${0.10 + 0.25 * (k - 0.5)})`;
      c.fillRect(x, y, 1, 1);
    }
    for (let i = 0; i < 90; i++) {
      const y = Math.floor(hash01(9000 + i * 4) * T) + 0.5, x = hash01(9001 + i * 4) * T, len = 8 + hash01(9002 + i * 4) * 70, k = hash01(9003 + i * 4);
      c.strokeStyle = k < 0.6 ? `rgba(255, 255, 255, ${0.10 + 0.18 * k})` : `rgba(150, 110, 80, ${0.02 + 0.04 * (k - 0.6)})`;
      c.lineWidth = 1;
      c.beginPath(); c.moveTo(x, y); c.lineTo(x + len, y); c.stroke();
    }
    textureTile = tile;
  }
  return ctx.createPattern(textureTile, 'repeat');
}

export interface PaperDrawOptions {
  params: EcgParams;
  lines: string[];
  /** CSS pixels per paper pixel (1 = 4 px per mm). */
  scale: number;
  /** Turn the sheet a quarter clockwise, for a phone held upright. */
  rotate?: boolean;
  dpr: number;
  colours?: { paper?: string; ink?: string };
}

/** CSS size of the drawn sheet. */
export function paperBox(lines: number, scale: number, rotate = false): { width: number; height: number } {
  const w = PAPER_WIDTH * scale, h = paperHeight(lines, scale) * scale;
  return rotate ? { width: h, height: w } : { width: w, height: h };
}

/** A line width in paper pixels that never draws thinner than `minDevice` device pixels, at `k` device pixels per paper pixel. */
export const lineWidthAt = (k: number) => (paperPx: number, minDevice: number) => Math.max(paperPx, minDevice / k);

/** Cream thermal paper with its grain and the 1 mm and 5 mm grid, filling W × H paper pixels. */
export function paintSheet(ctx: CanvasRenderingContext2D, W: number, H: number, paper: string, lw: (paperPx: number, minDevice: number) => number): void {
  ctx.fillStyle = paper;
  ctx.fillRect(0, 0, W, H);
  const pat = texture(ctx);
  if (pat) { ctx.fillStyle = pat; ctx.fillRect(0, 0, W, H); }
  ctx.lineWidth = lw(0.5, 0.6);
  ctx.strokeStyle = GRID_MINOR;
  ctx.beginPath();
  for (let x = 0; x <= W; x += MM) { ctx.moveTo(x + 0.25, 0); ctx.lineTo(x + 0.25, H); }
  for (let y = 0; y <= H; y += MM) { ctx.moveTo(0, y + 0.25); ctx.lineTo(W, y + 0.25); }
  ctx.stroke();
  ctx.lineWidth = lw(1, 1);
  ctx.strokeStyle = GRID_MAJOR;
  ctx.beginPath();
  for (let x = 0; x <= W; x += 5 * MM) { ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, H); }
  for (let y = 0; y <= H; y += 5 * MM) { ctx.moveTo(0, y + 0.5); ctx.lineTo(W, y + 0.5); }
  ctx.stroke();
}

/**
 * The pen: calibration pulses, traces with a soft thermal bleed under a crisp line, and lead names. A name is printed after the
 * traces at the top left of its cell, with a thin paper-coloured edge round each letter so a tall complex never makes it
 * unreadable; at `font` paper pixels (leadFont), so it stays readable on a phone.
 */
export function tracePen(ctx: CanvasRenderingContext2D, params: EcgParams, paper: string, ink: string, lw: (paperPx: number, minDevice: number) => number) {
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const calPulse = (base: number) => {
    ctx.strokeStyle = ink;
    ctx.lineWidth = lw(1.3, 1.1);
    ctx.beginPath();
    ctx.moveTo(6, base); ctx.lineTo(8, base); ctx.lineTo(8, base - PX_MV); ctx.lineTo(28, base - PX_MV); ctx.lineTo(28, base); ctx.lineTo(X0, base);
    ctx.stroke();
  };
  /** A lead name with its baseline `font` px under the cell's top edge `top`, starting at `x`. */
  const label = (txt: string, x: number, top: number, font = LEAD_FONT) => {
    const y = top + 4 + Math.round(0.86 * font);
    ctx.font = `500 ${font}px ${FONT_STACK}`;
    ctx.textBaseline = 'alphabetic';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = paper;
    ctx.lineWidth = Math.max(lw(2.5, 2), font * 0.24);
    ctx.globalAlpha = 0.92;
    ctx.strokeText(txt, x, y);
    ctx.globalAlpha = 1;
    ctx.fillStyle = ink;
    ctx.fillText(txt, x, y);
  };
  const path = (lead: Lead, x: number, base: number, t0: number, dur: number) => {
    ctx.beginPath();
    const n = Math.round(dur * PX_S * 2);
    for (let i = 0; i <= n; i++) {
      const px = i / 2;
      const y = base - sampleLead(params, t0 + px / PX_S, lead) * PX_MV;
      if (i === 0) ctx.moveTo(x + px, y); else ctx.lineTo(x + px, y);
    }
  };
  const strip = (lead: Lead, x: number, base: number, t0: number, dur: number) => {
    path(lead, x, base, t0, dur);
    ctx.strokeStyle = 'rgba(20, 24, 32, 0.14)';
    ctx.lineWidth = lw(2.8, 2.2);
    ctx.stroke();
    ctx.strokeStyle = ink;
    ctx.lineWidth = lw(1.4, 1.25);
    ctx.stroke();
  };
  const divider = (x: number, base: number) => {
    ctx.strokeStyle = ink;
    ctx.lineWidth = lw(1, 1);
    ctx.beginPath(); ctx.moveTo(x + 0.5, base - 4); ctx.lineTo(x + 0.5, base + 4); ctx.stroke();
  };
  return { calPulse, label, strip, divider };
}

/** Sizes the canvas for a sheet of W × H paper pixels drawn at `scale` (turned a quarter when asked) and sets the transform. */
export function prepareCanvas(cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D, box: { width: number; height: number }, scale: number, dpr: number, rotate = false): number {
  const devW = Math.max(1, Math.round(box.width * dpr)), devH = Math.max(1, Math.round(box.height * dpr));
  if (cv.width !== devW || cv.height !== devH) { cv.width = devW; cv.height = devH; }
  cv.style.width = `${box.width}px`;
  cv.style.height = `${box.height}px`;
  const k = scale * dpr;
  if (rotate) ctx.setTransform(0, k, -k, 0, devW, 0);
  else ctx.setTransform(k, 0, 0, k, 0, 0);
  return k;
}

export function drawPaper(cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D, o: PaperDrawOptions): void {
  const W = PAPER_WIDTH;
  const H = paperHeight(o.lines.length, o.scale);
  const HEAD = headHeight(o.lines.length, o.scale);
  const k = prepareCanvas(cv, ctx, paperBox(o.lines.length, o.scale, o.rotate), o.scale, o.dpr, o.rotate);
  const lw = lineWidthAt(k);
  const paper = o.colours?.paper ?? '#FFFDF7';
  const ink = o.colours?.ink ?? '#1F2733';
  paintSheet(ctx, W, H, paper, lw);

  const font = headerFont(o.scale);
  const line = (font * HEAD_LINE) / HEAD_FONT;
  ctx.fillStyle = ink;
  ctx.font = `500 ${font}px ${FONT_STACK}`;
  ctx.textBaseline = 'alphabetic';
  o.lines.forEach((text, i) => ctx.fillText(text, 12, 8 + (font * 10) / HEAD_FONT + i * line));

  const pen = tracePen(ctx, o.params, paper, ink, lw);
  for (let r = 0; r < 3; r++) {
    const top = HEAD + r * ROW;
    const base = top + Math.round(ROW * 0.6);
    pen.calPulse(base);
    for (let c = 0; c < 4; c++) {
      const sx = X0 + c * SEG;
      const lead = ROWS[r][c];
      if (c > 0) pen.divider(sx, base);
      pen.strip(lead, sx, base, c * SEG_S, SEG_S);
    }
  }
  const rtop = HEAD + 3 * ROW;
  const rbase = rtop + Math.round(ROW * 0.6);
  pen.calPulse(rbase);
  pen.strip('II', X0, rbase, 0, 4 * SEG_S);
  // The names last, so no trace (a tall complex from the row below included) is drawn over one.
  const nameFont = leadFont(o.scale);
  for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) pen.label(ROWS[r][c], X0 + c * SEG + 8, HEAD + r * ROW, nameFont);
  pen.label('II', X0 + 8, rtop, nameFont);
}

// ---------------------------------------------------------------------------------------------------------------
// The lead grid (v2.2, tap a lead): the twelve leads in two columns, limb leads on the left (I, II, III, aVR, aVL,
// aVF) and chest leads on the right (V1 to V6), 2.5 s each at the same calibration. A recognised machine layout
// that gives every lead a cell big enough to tap on a phone. The lead names are set by the page over the canvas.
// ---------------------------------------------------------------------------------------------------------------
export const GRID_COLUMNS: readonly (readonly Lead[])[] = [['I', 'II', 'III', 'aVR', 'aVL', 'aVF'], ['V1', 'V2', 'V3', 'V4', 'V5', 'V6']];
export const LEAD_GRID_WIDTH = X0 + 2 * SEG + 2 * MM;
const GRID_HEAD = 7 * MM;

/** Paper pixels per row: at least 19 mm, and tall enough that a row is at least `minCss` CSS pixels at this scale. */
export function leadGridRow(scale: number, minCss = 46): number {
  const s = Number.isFinite(scale) && scale > 0 ? scale : 0.66;
  return Math.max(19 * MM, Math.ceil(minCss / s / MM) * MM);
}
export const leadGridHeight = (rowPx: number) => GRID_HEAD + 6 * rowPx + 2 * MM;

export interface Cell { x: number; y: number; w: number; h: number }
/** Each lead's cell on the sheet, in paper pixels. The left column takes in the calibration pulse. */
export function leadCells(rowPx: number): Record<Lead, Cell> {
  const out = {} as Record<Lead, Cell>;
  GRID_COLUMNS.forEach((col, c) => col.forEach((lead, r) => {
    const x = c === 0 ? 0 : X0 + SEG;
    const w = c === 0 ? X0 + SEG : LEAD_GRID_WIDTH - (X0 + SEG);
    out[lead] = { x, y: GRID_HEAD + r * rowPx, w, h: rowPx };
  }));
  return out;
}

/** The one header line of the grid: the settings and the rate, nothing that gives the answer. */
export function gridHeader(params: Pick<EcgParams, 'rate' | 'rhythm'>): string {
  const { rate } = measure(params);
  return `${SETTINGS.replace(/\s{2,}/g, '  ')}   Vent. rate ${rate === undefined ? NONE : `${rate} bpm`}`;
}

export interface LeadGridDrawOptions { params: EcgParams; scale: number; rowPx: number; dpr: number; colours?: { paper?: string; ink?: string } }

export function drawLeadGrid(cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D, o: LeadGridDrawOptions): void {
  const W = LEAD_GRID_WIDTH;
  const H = leadGridHeight(o.rowPx);
  const k = prepareCanvas(cv, ctx, { width: W * o.scale, height: H * o.scale }, o.scale, o.dpr);
  const lw = lineWidthAt(k);
  const paper = o.colours?.paper ?? '#FFFDF7';
  const ink = o.colours?.ink ?? '#1F2733';
  paintSheet(ctx, W, H, paper, lw);
  ctx.fillStyle = ink;
  ctx.font = PAPER_FONT;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(gridHeader(o.params), 12, 19);
  const pen = tracePen(ctx, o.params, paper, ink, lw);
  GRID_COLUMNS.forEach((col, c) => col.forEach((lead, r) => {
    const top = GRID_HEAD + r * o.rowPx;
    const base = top + Math.round(o.rowPx * 0.62);
    const sx = X0 + c * SEG;
    if (c === 0) pen.calPulse(base); else pen.divider(sx, base);
    pen.strip(lead, sx, base, c * SEG_S, SEG_S);
  }));
}
